import { useCallback, useEffect, useState } from 'react';

import { keys as LocalStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const SPOTLIGHT_SEEN_LOCAL_STORAGE_KEY = LocalStorageKeys.NAVX_UC_CWR_EXPERIMENT_SPOTLIGHT_SEEN;

// In-memory flag to survive remounts in the same tab and guard against
// repeated writes. Expressed as a plain object so React Compiler treats it
// as a ref-like mutable container rather than a bare variable mutation.
const hasSeen = { current: false };

// Test-only reset.
export const __resetSessionHasSeenSpotlightForTests = (): void => {
	hasSeen.current = false;
};

export type UseCwRSpotlightSeenStorageResult = {
	hasSeenSpotlight: boolean;
	markSpotlightSeen: () => void;
};

export const useCwRSpotlightSeenStorage = (): UseCwRSpotlightSeenStorageResult => {
	// Default to true (assume seen) so the spotlight stays hidden until we confirm
	const [hasSeenSpotlight, setHasSeenSpotlight] = useState<boolean>(true);

	useEffect(function syncFromStorage() {
		if (!hasSeen.current) {
			try {
				hasSeen.current =
					confluenceLocalStorageInstance?.getItem(SPOTLIGHT_SEEN_LOCAL_STORAGE_KEY) === 'true';
			} catch {
				hasSeen.current = false;
			}
		}

		// hasSeen.current is a module-level object. The next if reads it synchronously in the same tick.
		if (!hasSeen.current) {
			setHasSeenSpotlight(false);
		}
	}, []);

	const markSpotlightSeen = useCallback(() => {
		if (hasSeen.current) {
			return;
		}
		hasSeen.current = true;
		setHasSeenSpotlight(true);
		try {
			confluenceLocalStorageInstance?.setItem(SPOTLIGHT_SEEN_LOCAL_STORAGE_KEY, 'true');
		} catch {
			// Swallow storage errors — the in-memory flag already guards against re-showing.
		}
	}, []);

	return {
		hasSeenSpotlight,
		markSpotlightSeen,
	};
};
